import { withLangPrefix, type Lang } from './lang'

const SITE_ORIGIN = 'https://gigaette.com'

/**
 * Fallback share card for pages that don't supply their own image — 1200x630,
 * the ratio social scrapers expect. Built by `node scripts/generate-og.mjs`.
 *
 * JPEG rather than WebP on purpose: WebP og:image support is inconsistent across
 * scrapers (notably KakaoTalk and LinkedIn), and this is the image behind
 * almost every share of the site.
 */
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og/gigaette-og.jpg`

/**
 * Google truncates SERP snippets around 160 characters, so anything longer is
 * cut mid-word anyway. Several routes pass a full content paragraph as their
 * description (application cases ran 267–442 chars), which Ahrefs flags as
 * "meta description too long".
 *
 * Clamp to whole sentences that fit, which reads better than Google's mid-word
 * cut. Only shortens — a description already within budget is untouched, and
 * short ones are never padded (Korean says the same thing in roughly half the
 * characters of English, so /ko/ descriptions are legitimately shorter).
 */
export function clampMetaDescription(text: string, max = 160, min = 110): string {
  const s = text.trim()
  if (s.length <= max) return s

  // Sentence ends: Latin ./!/? and the Korean declarative "…다." both land on
  // a period, so splitting after [.!?] covers en and ko alike.
  const sentences = s.match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? []
  let out = ''
  for (const sentence of sentences) {
    if ((out + sentence).trim().length > max) break
    out += sentence
  }
  out = out.trim()

  // Whole sentences are preferred, but only when they still fill the snippet.
  // Cutting a 340-char paragraph down to its 77-char opening sentence just
  // trades "too long" for "too short" and wastes SERP real estate, so in that
  // case fall back to a word-boundary cut that lands inside [min, max].
  if (out.length >= min) return out

  const cut = s.slice(0, max - 1)
  const sp = cut.lastIndexOf(' ')
  return (sp > min ? cut.slice(0, sp) : cut).trimEnd() + '…'
}

interface SEOData {
  title: string
  description: string
  /** Language-independent logical path (no /ko prefix). */
  path: string
  /** Active language; drives canonical + hreflang. */
  lang: Lang
  image?: string
  type?: 'website' | 'article'
}

export function updateSEO(data: SEOData) {
  const { title, path, lang, image, type = 'website' } = data
  const description = clampMetaDescription(data.description)

  const canonical = `${SITE_ORIGIN}${withLangPrefix(path, lang)}`

  // Update title. Route titles already include the brand ("… - GIGAette"),
  // so we use the value as-is to avoid a doubled "… - GIGAette | GIGAette".
  document.title = title

  // Update or create meta tags
  updateMetaTag('description', description)
  updateMetaTag('og:title', title, 'property')
  updateMetaTag('og:description', description, 'property')
  updateMetaTag('og:type', type, 'property')
  updateMetaTag('og:url', canonical, 'property')
  updateMetaTag('og:locale', lang === 'ko' ? 'ko_KR' : 'en_US', 'property')

  // Always set it. Emitting it only when a page supplies an image left the
  // previous page's card in place across SPA navigation, so e.g. leaving a blog
  // post carried its photo onto every page visited afterwards.
  const ogImage = image
    ? image.startsWith('http')
      ? image
      : `${SITE_ORIGIN}${image}`
    : DEFAULT_OG_IMAGE
  updateMetaTag('og:image', ogImage, 'property')
  updateMetaTag('twitter:image', ogImage)

  updateMetaTag('twitter:card', 'summary_large_image')
  updateMetaTag('twitter:title', title)
  updateMetaTag('twitter:description', description)

  // Update canonical + hreflang alternates
  updateCanonicalLink(canonical)
  updateAlternateLinks(path)

  // Keep the <html lang> attribute in sync (also done by i18n.setLanguage)
  document.documentElement.lang = lang
}

/**
 * Declare the en/ko/x-default alternates for the current logical path so
 * crawlers connect the two language versions.
 */
function updateAlternateLinks(logicalPath: string) {
  const alternates: Array<{ hreflang: string; href: string }> = [
    { hreflang: 'en', href: `${SITE_ORIGIN}${withLangPrefix(logicalPath, 'en')}` },
    { hreflang: 'ko', href: `${SITE_ORIGIN}${withLangPrefix(logicalPath, 'ko')}` },
    { hreflang: 'x-default', href: `${SITE_ORIGIN}${withLangPrefix(logicalPath, 'en')}` },
  ]

  for (const { hreflang, href } of alternates) {
    let link = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`)
    if (!link) {
      link = document.createElement('link')
      link.setAttribute('rel', 'alternate')
      link.setAttribute('hreflang', hreflang)
      document.head.appendChild(link)
    }
    link.setAttribute('href', href)
  }
}

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let element = document.querySelector(`meta[${attribute}="${name}"]`)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function updateCanonicalLink(url: string) {
  let link = document.querySelector('link[rel="canonical"]')

  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    document.head.appendChild(link)
  }

  link.setAttribute('href', url)
}

export function addStructuredData(data: object, id?: string) {
  // When an id is given, the block is idempotent: re-running replaces the
  // existing one instead of appending a duplicate. This matters during
  // prerendering, where unbuilt routes are served the home snapshot (which
  // already contains the baked schema) before the SPA boots and re-adds it.
  if (id) {
    document
      .querySelectorAll(`script[type="application/ld+json"][data-schema="${id}"]`)
      .forEach((el) => el.remove())
  }
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  if (id) script.setAttribute('data-schema', id)
  script.textContent = JSON.stringify(data)
  document.head.appendChild(script)
}

// Add organization structured data on init
export function initOrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GIGAette',
    description: 'Decarbonizing Industrial Heat with Thermal Energy Storage',
    url: 'https://gigaette.com',
    logo: 'https://gigaette.com/logo.png',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Sales',
      email: 'contact@gigaette.com',
    },
  }

  addStructuredData(schema, 'organization')
}
